import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import styles from "../../assets/styles/blog/SingleBlog.module.css";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/blog-details`;

const resolveImageSrc = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const splitParagraphs = (value = "") =>
  String(value)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

const splitListItems = (value = "") =>
  String(value)
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

const stripHtml = (value = "") => String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "";

const SingleBlog = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.get(`${API}/slug/${slug}`);
        setPost(res.data?.data || null);
      } catch (err) {
        console.error(err);
        setPost(null);
        setError(err.response?.status === 404 ? "Blog post not found." : "Failed to load blog post.");
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const steps = useMemo(() => {
    if (!post) return [];
    return [
      {
        title: post.step1Title,
        desc: post.step1Desc,
        tipHeading: post.stepTip1Heading,
        tipParagraph: post.step1Tip1Paragraph,
      },
      {
        title: post.step2Title,
        desc: post.step2Desc,
        tipHeading: post.stepTip2Heading,
        tipParagraph: post.stepTip2Paragraph,
      },
      {
        title: post.step3Title,
        desc: post.step3Desc,
        tipHeading: post.stepTip3Heading,
        tipParagraph: post.step1Tip3Paragraph,
      },
      {
        title: post.step4Title,
        desc: post.step4Desc,
        tipHeading: post.stepTip4Heading,
        tipParagraph: post.step1Tip4Paragraph,
      },
      {
        title: post.step5Title,
        desc: post.step5Desc,
        tipHeading: post.stepTip5Heading,
        tipParagraph: post.step1Tip5Paragraph,
      },
    ].filter((item) => item.title || item.desc || item.tipHeading || item.tipParagraph);
  }, [post]);

  const benefits = useMemo(() => {
    if (!post) return [];
    return [1, 2, 3, 4, 5]
      .map((index) => ({
        icon: post[`benefit${index}Icon`],
        title: post[`benefit${index}Title`],
        desc: post[`benefit${index}Desc`],
      }))
      .filter((item) => item.title || item.desc || item.icon);
  }, [post]);

  const whoItems = useMemo(() => {
    if (!post) return [];
    return [1, 2, 3, 4]
      .map((index) => ({
        title: post[`who${index}Title`],
        desc: post[`who${index}Des`],
      }))
      .filter((item) => item.title || item.desc);
  }, [post]);

  const dailyItems = useMemo(() => {
    if (!post) return [];
    return [1, 2, 3, 4]
      .map((index) => ({
        title: post[`daily${index}Title`],
        desc: post[`daily${index}Desc`],
      }))
      .filter((item) => item.title || item.desc);
  }, [post]);

  const chooseItems = useMemo(() => {
    if (!post) return [];
    return [post.choose1, post.choose2, post.choose3, post.choose4, post.choose5, post.choose6].filter(Boolean);
  }, [post]);

  const faqItems = useMemo(() => {
    if (!post) return [];
    return [1, 2, 3, 4]
      .map((index) => ({
        question: post[`faq${index}Q`],
        answer: post[`faq${index}A`],
      }))
      .filter((item) => item.question || item.answer);
  }, [post]);

  const warningItems = useMemo(() => splitListItems(post?.precautionsText || ""), [post]);
  const whatParagraphs = useMemo(() => splitParagraphs(post?.whatParagraph || ""), [post]);
  const whatDescriptionParagraphs = useMemo(() => splitParagraphs(post?.whatDescription || ""), [post]);
  const whyParagraphs = useMemo(() => splitParagraphs(post?.whyDescription || ""), [post]);
  const chooseParagraphs = useMemo(() => splitParagraphs(post?.chooseTitle || ""), [post]);
  const conclusionParagraphs = useMemo(() => splitParagraphs(post?.conclusion || ""), [post]);
  const dateLabel = useMemo(() => formatDate(post?.updatedAt || post?.createdAt), [post]);

  if (loading) {
    return <section className={styles.wrapper}><div className={styles.contentSection}>Loading blog post...</div></section>;
  }

  if (!post) {
    return (
      <section className={styles.wrapper}>
        <div className={styles.contentSection}>
          <article className={styles.contentCard}>
            <h1 className={styles.pageTitle}>Blog Post</h1>
            <p>{error || "Blog post not found."}</p>
            <Link to="/blog">Back to all blogs</Link>
          </article>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.wrapper}>
      <div className={styles.heroSection}>
        {post.heroImage ? (
          <img
            src={resolveImageSrc(post.heroImage)}
            alt={post.heroTitle}
            className={styles.heroBackgroundImage}
          />
        ) : null}
        <div className={styles.heroContent}>
          <span className={styles.badge}>{post.category || "Blog"}</span>
          <h1 className={styles.pageTitle}>{post.heroTitle}</h1>
          <p className={styles.heroSubtitle}>{post.heroSubtitle}</p>
          <div className={styles.breadcrumb}>
            <Link to="/blog">Blog</Link> <span>/</span> {post.breadcrumb || post.category || "Article"}
          </div>
          {dateLabel ? <p className={styles.heroSubtitle}>{dateLabel}</p> : null}
        </div>
      </div>

      <div className={styles.contentSection}>
        <article className={styles.contentCard}>
          {post.BlogMainImage ? (
            <img
              src={resolveImageSrc(post.BlogMainImage)}
              alt={post.heroTitle}
              className="img-fluid rounded mb-4"
            />
          ) : null}

          {(post.introQuote || post.introText) && (
            <div className={styles.introSection}>
              {post.introQuote ? <p className={styles.intro}>{post.introQuote}</p> : null}
              {post.introText ? <p className={styles.contextText}>{post.introText}</p> : null}
            </div>
          )}

          {(post.whatHeading || post.whatTitle || whatParagraphs.length || whatDescriptionParagraphs.length) && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{post.whatHeading || post.whatTitle}</h2>
              <div className={styles.sectionContent}>
                {whatParagraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {post.whatTitle && post.whatTitle !== post.whatHeading ? <h3>{post.whatTitle}</h3> : null}
                {whatDescriptionParagraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          {(post.stepHeading || post.stepparagraph || steps.length) && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{post.stepHeading}</h2>
              {post.stepparagraph ? <p className={styles.sectionIntro}>{post.stepparagraph}</p> : null}
              <div className={styles.stepsGrid}>
                {steps.map((step, index) => (
                  <div className={styles.stepCard} key={`${step.title}-${index}`}>
                    <div className={styles.stepNumber}>{index + 1}</div>
                    <div className={styles.stepContent}>
                      {step.title ? <h3>{step.title}</h3> : null}
                      {step.desc ? <p>{step.desc}</p> : null}
                      {step.tipHeading || step.tipParagraph ? (
                        <p className={styles.proTip}>
                          {step.tipHeading ? <strong>{step.tipHeading}:</strong> : null}{" "}
                          {step.tipParagraph}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(post.benefitHeading || post.benefitparagraph || benefits.length) && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{post.benefitHeading}</h2>
              {post.benefitparagraph ? <p className={styles.sectionIntro}>{post.benefitparagraph}</p> : null}
              <div className={styles.benefitsGrid}>
                {benefits.map((benefit) => (
                  <div className={styles.benefitCard} key={`${benefit.title}-${benefit.icon}`}>
                    {benefit.icon ? <div className={styles.benefitIcon}>{benefit.icon}</div> : null}
                    {benefit.title ? <h3>{benefit.title}</h3> : null}
                    {benefit.desc ? <p>{benefit.desc}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.divider}></div>

          {(post.whyTitle || whyParagraphs.length) && (
            <div className={styles.highlightSection}>
              <h2>{post.whyTitle}</h2>
              {whyParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          )}

          {(post.whoTitle || whoItems.length) && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{post.whoTitle}</h2>
              <ul className={styles.audienceList}>
                {whoItems.map((item) => (
                  <li key={`${item.title}-${item.desc}`}>
                    {item.title ? <strong>{item.title}:</strong> : null} {item.desc}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(post.dailyMainTitle || dailyItems.length) && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>{post.dailyMainTitle}</h2>
              <div className={styles.integrationList}>
                {dailyItems.map((item) => (
                  <div className={styles.integrationItem} key={`${item.title}-${item.desc}`}>
                    {item.title ? <div className={styles.timeLabel}>{item.title}</div> : null}
                    {item.desc ? <p>{item.desc}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(post.precautionsTitle || warningItems.length || post.precautionsText) && (
            <div className={styles.warningBox}>
              <h3>{post.precautionsTitle}</h3>
              {warningItems.length ? (
                <ul className={styles.warningList}>
                  {warningItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>{post.precautionsText}</p>
              )}
            </div>
          )}

          {(post.chooseMainTitle || chooseParagraphs.length || chooseItems.length) && (
            <div className={styles.ctaSection}>
              <h2>{post.chooseMainTitle}</h2>
              {chooseParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              <div className={styles.ctaFeatures}>
                {chooseItems.map((item) => (
                  <div className={styles.feature} key={item}>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(post.conclusionMainTitle || conclusionParagraphs.length) && (
            <div className={styles.conclusion}>
              <h2>{post.conclusionMainTitle}</h2>
              {conclusionParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          )}

          {faqItems.length ? (
            <div className={styles.faqSection}>
              <h2 className={styles.faqTitle}>{`${stripHtml(post.heroTitle)} FAQ`}</h2>
              <div className={styles.faqGrid}>
                {faqItems.map((item) => (
                  <div className={styles.faqItem} key={item.question}>
                    {item.question ? <h3>{item.question}</h3> : null}
                    {item.answer ? <p>{item.answer}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </article>
      </div>
    </section>
  );
};

export default SingleBlog;
